import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  LoadingOverlay,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { IconEdit, IconPlus, IconSearch, IconTrash } from "@tabler/icons-react";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";
import { INT_MAX, setActionLog } from "../utils/util";
import { ROW_HOVER_BG } from "../constants/colors";
import { useModal } from "../contexts/useModal";
import { TerminalModelGenerate, TerminalModelUpdate } from "../components/Modal";
import { useCanWrite } from "../utils/useCanWrite";

interface TerminalModelRow {
  terminalModelId: number;
  internalModelName: string;
  externalModelName: string;
  serialKeyInjectType: number;
  vanType: number;
  terminalColor?: string;
  useYn: string;
  createdDate?: string;
  updatedDate?: string;
}

type CodeOption = { value: string; label: string };

const GRADIENT_PROPS = {
  variant: "gradient" as const,
  gradient: { from: "#e0eafc", to: "#b6e0fe", deg: 180 },
  c: "#141414",
};

const SEARCH_BTN_STYLE: React.CSSProperties = {
  backgroundColor: "#8FC0CF",
  color: "#fff",
};

const formatDate = (value?: string) => {
  if (!value) return "-";
  const d = dayjs(value);
  return d.isValid() ? d.format("YYYY-MM-DD HH:mm:ss") : value;
};

const TerminalModels: React.FC<{ menuTitle: string }> = ({ menuTitle }) => {
  const canWrite = useCanWrite();
  const [rows, setRows] = useState<TerminalModelRow[]>([]);
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(false);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const [selectedRow, setSelectedRow] = useState<TerminalModelRow | null>(null);

  const [injectTypeOptions, setInjectTypeOptions] = useState<CodeOption[]>([]);
  const [vanTypeOptions, setVanTypeOptions] = useState<CodeOption[]>([]);

  const { showModal } = useModal();

  const injectLabelMap = useMemo(
    () => Object.fromEntries(injectTypeOptions.map((o) => [o.value, o.label])),
    [injectTypeOptions],
  );
  const vanLabelMap = useMemo(
    () => Object.fromEntries(vanTypeOptions.map((o) => [o.value, o.label])),
    [vanTypeOptions],
  );

  const loadCodeOptions = useCallback(async () => {
    try {
      const [injectRes, vanRes] = await Promise.all([
        axiosInstance.get("/api/v1/common/serial-key-inject-type"),
        axiosInstance.get("/api/v1/common/van"),
      ]);
      if (
        injectRes.data?.code === "200" &&
        Array.isArray(injectRes.data.data)
      ) {
        const opts = injectRes.data.data.map(
          (item: { code: string; name: string }) => ({
            value: item.code,
            label: `${item.code}: ${item.name}`,
          }),
        );
        setInjectTypeOptions(opts);
      }
      if (vanRes.data?.code === "200" && Array.isArray(vanRes.data.data)) {
        const opts = vanRes.data.data.map(
          (item: { code: string; name: string }) => ({
            value: item.code,
            label: item.name,
          }),
        );
        setVanTypeOptions(opts);
      }
    } catch {
      toast.error("공통 코드 조회에 실패하였습니다.");
    }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = {
        currentPageNo: 1,
        recordsPerPage: INT_MAX,
      };
      const q = keyword.trim();
      if (q) params.keyword = q;

      const res = await axiosInstance.get("/api/v1/terminal/model/list", {
        params,
      });
      if (res.data?.code === "200") {
        setRows(res.data.data?.list ?? []);
      } else {
        setRows([]);
        toast.error(res.data?.msg || "조회에 실패하였습니다.");
      }
    } catch {
      toast.error("조회에 실패하였습니다.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [keyword]);

  useEffect(() => {
    loadCodeOptions();
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTerminalModelGenerate = async () => {
    await setActionLog({
      eventType: "BUTTON_CLICK",
      title: "단말기 모델 등록 버튼 클릭",
    });
    const flag = await showModal({
      component: TerminalModelGenerate,
      props: {},
    });
    if (flag) {
      toast.success("단말기 모델이 등록되었습니다.");
      setSelectedRow(null);
      fetchList();
    }
  };

  const handleTerminalModelUpdate = async () => {
    if (!selectedRow) {
      toast.error("수정할 모델을 목록에서 선택하세요.");
      return;
    }
    await setActionLog({
      eventType: "BUTTON_CLICK",
      title: "단말기 모델 수정 버튼 클릭",
      content: selectedRow,
    });
    const flag = await showModal({
      component: TerminalModelUpdate,
      props: { row: selectedRow },
    });
    if (flag) {
      toast.success("사용 여부가 수정되었습니다.");
      setSelectedRow(null);
      fetchList();
    }
  };

  const handleSearch = async () => {
    await setActionLog({
      eventType: "BUTTON_CLICK",
      title: "단말기 모델 조회 버튼 클릭",
    });
    fetchList();
  };

  const handleDelete = async (terminalModelId: number) => {
    if (!window.confirm("선택한 모델을 삭제하시겠습니까?")) return;

    try {
      const res = await axiosInstance.delete("/api/v1/terminal/model", {
        params: { terminalModelId },
      });
      if (res.data?.code === "200") {
        toast.success("단말기 모델이 삭제되었습니다.");
        if (selectedRow?.terminalModelId === terminalModelId) {
          setSelectedRow(null);
        }
        fetchList();
      } else {
        toast.error(res.data?.msg || "삭제에 실패하였습니다.");
      }
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { msg?: string } } })?.response?.data
          ?.msg || "삭제에 실패하였습니다.";
      toast.error(msg);
    }
  };

  return (
    <Flex
      bg="#fafbfc"
      bdrs={12}
      p="0.75rem 1.5rem"
      direction="column"
      gap="md"
      pos="relative"
    >
      <LoadingOverlay visible={loading} zIndex={200} />

      <Flex align="center" justify="space-between">
        <Box fz="1.75rem" fw={700}>
          {menuTitle}
        </Box>
        <Flex gap="xs">
          <Button
            leftSection={<IconPlus size={20} stroke={1.5} />}
            onClick={handleTerminalModelGenerate}
            disabled={loading || !canWrite}
            {...GRADIENT_PROPS}
          >
            등록
          </Button>
          <Button
            leftSection={<IconEdit size={20} stroke={1.5} />}
            onClick={handleTerminalModelUpdate}
            disabled={loading || selectedRow == null || !canWrite}
            {...GRADIENT_PROPS}
          >
            수정
          </Button>
        </Flex>
      </Flex>

      <Text size="sm" c="dimmed">
        등록·수정은 모달에서 진행합니다. 목록 행을 선택한 뒤 수정하면 사용
        여부(use_yn)만 변경할 수 있습니다.
      </Text>

      <Flex justify="space-between" align="end" gap="sm" wrap="wrap">
        <Text fw={600}>모델 목록 ({rows.length})</Text>
        <Flex gap="xs" align="end">
          <TextInput
            placeholder="내부/외부 모델명 검색"
            value={keyword}
            onChange={(e) => setKeyword(e.currentTarget.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            style={{ minWidth: 280 }}
          />
          <Button
            leftSection={<IconSearch size={20} stroke={1.5} />}
            onClick={handleSearch}
            disabled={loading}
            variant="filled"
            style={SEARCH_BTN_STYLE}
          >
            조회
          </Button>
        </Flex>
      </Flex>

      <Table
        striped
        highlightOnHover
        withTableBorder
        styles={{ table: { tableLayout: "fixed", width: "100%" } }}
      >
        <Table.Thead>
          <Table.Tr bg="#f5f6fa">
            <Table.Th w={56} p={12}>
              ID
            </Table.Th>
            <Table.Th w="12%" p={12}>
              내부모델
            </Table.Th>
            <Table.Th w="12%" p={12}>
              외부모델
            </Table.Th>
            <Table.Th w={240} p={12}>
              시리얼 키 주입 타입
            </Table.Th>
            <Table.Th w={88} p={12}>
              VAN
            </Table.Th>
            <Table.Th w={72} p={12} ta="center">
              색상
            </Table.Th>
            <Table.Th w={56} p={12} ta="center">
              사용
            </Table.Th>
            <Table.Th w={168} p={12}>
              수정일시
            </Table.Th>
            <Table.Th w={88} p={12} ta="center">
              삭제
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.length === 0 ? (
            <Table.Tr>
              <Table.Td colSpan={9}>
                <Text size="sm" c="dimmed" ta="center" py="sm">
                  등록된 모델이 없습니다.
                </Text>
              </Table.Td>
            </Table.Tr>
          ) : (
            rows.map((row, idx) => {
              const isSelected =
                selectedRow?.terminalModelId === row.terminalModelId;
              return (
                <Table.Tr
                  key={row.terminalModelId}
                  onClick={() =>
                    setSelectedRow((prev) =>
                      prev?.terminalModelId === row.terminalModelId
                        ? null
                        : row,
                    )
                  }
                  bg={
                    isSelected
                      ? "var(--mantine-color-blue-light)"
                      : hoveredRow === idx
                        ? ROW_HOVER_BG
                        : undefined
                  }
                  fw={isSelected ? 700 : undefined}
                  style={{ cursor: "pointer" }}
                  onMouseEnter={() => setHoveredRow(idx)}
                  onMouseLeave={() => setHoveredRow(null)}
                >
                  <Table.Td p={12}>{row.terminalModelId}</Table.Td>
                  <Table.Td p={12} style={{ wordBreak: "break-all" }}>
                    {row.internalModelName}
                  </Table.Td>
                  <Table.Td p={12} style={{ wordBreak: "break-all" }}>
                    {row.externalModelName}
                  </Table.Td>
                  <Table.Td p={12} style={{ wordBreak: "break-word" }}>
                    {injectLabelMap[String(row.serialKeyInjectType)] ??
                      row.serialKeyInjectType}
                  </Table.Td>
                  <Table.Td p={12}>
                    {vanLabelMap[String(row.vanType)] ?? row.vanType}
                  </Table.Td>
                  <Table.Td p={12} ta="center">
                    {row.terminalColor || "-"}
                  </Table.Td>
                  <Table.Td p={12} ta="center">
                    {row.useYn}
                  </Table.Td>
                  <Table.Td p={12}>{formatDate(row.updatedDate)}</Table.Td>
                  <Table.Td p={12} ta="center">
                    <Flex justify="center">
                      <Button
                        size="xs"
                        color="red"
                        leftSection={<IconTrash size={14} stroke={1.5} />}
                        disabled={!canWrite}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(row.terminalModelId);
                        }}
                      >
                        삭제
                      </Button>
                    </Flex>
                  </Table.Td>
                </Table.Tr>
              );
            })
          )}
        </Table.Tbody>
      </Table>
    </Flex>
  );
};

export default TerminalModels;
